/**
 * Passo 1 di "Scrivi a piu' persone": a chi scrivere.
 * Sorgenti: Lead selezionati nella lista, una fase della pipeline, un'etichetta chat.
 */
import { useEffect } from 'react';
import { Loader2, Users } from 'lucide-react';
import { useLeadPipelineStore } from '@/lib/stores/leadPipelineStore';
import { useWhatsAppChatMetaStore } from '@/lib/stores/whatsappChatMetaStore';
import { EXCLUSION_LABELS, type ExclusionReason } from '@/lib/services/whatsapp/bulkSend/bulkRecipients';
import { BULK_LIMITS } from '@/lib/services/whatsapp/bulkSend/bulkSchedule';
import { tip } from '@/components/messages/ChatTooltip';
import type { AudienceSource, BulkSendState } from './useBulkSend';

const selectCls = 'w-full px-2.5 py-1.5 text-sm rounded-md border border-[rgba(55,53,47,0.16)] dark:border-white/10 bg-white dark:bg-[#1f1f22] text-[#37352F] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#2383E2]/30';

export function BulkAudienceStep({ s }: { s: BulkSendState }) {
  const pipelines = useLeadPipelineStore((st) => st.pipelines);
  const fetchPipelines = useLeadPipelineStore((st) => st.fetchPipelines);
  const getStages = useLeadPipelineStore((st) => st.getStagesForPipeline);
  const labels = useWhatsAppChatMetaStore((st) => st.orgLabels);
  const fetchOrgLabels = useWhatsAppChatMetaStore((st) => st.fetchOrgLabels);

  useEffect(() => {
    if (s.canUseStages && pipelines.length === 0) void fetchPipelines();
    if (s.organizationId && labels.length === 0) void fetchOrgLabels(s.organizationId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.organizationId, s.canUseStages]);

  const stages = s.pipelineId ? getStages(s.pipelineId) : [];
  const sources: Array<{ id: AudienceSource; label: string; hint: string; show: boolean }> = [
    { id: 'selezione', label: 'Lead selezionati', hint: 'I Lead scelti nella lista', show: s.hasSelection },
    { id: 'fase', label: 'Fase della pipeline', hint: 'Tutti i Lead che vedi in una fase', show: s.canUseStages },
    { id: 'etichetta', label: 'Etichetta chat', hint: "Le chat con un'etichetta", show: true },
  ];

  const onStage = (id: string) => {
    s.setStageId(id);
    const p = pipelines.find((x) => x.id === s.pipelineId);
    const st = stages.find((x) => x.id === id);
    s.setAudienceLabel(st ? `Fase: ${st.name}${p ? ` (${p.name})` : ''}` : '');
  };
  const onLabel = (id: string) => {
    s.setLabelId(id);
    const l = labels.find((x) => x.id === id);
    s.setAudienceLabel(l ? `Etichetta chat: ${l.name}` : '');
  };

  const { recipients, summary } = s.built;
  const max = BULK_LIMITS[s.channel].max;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-1.5">
        {sources.filter((x) => x.show).map((x) => (
          <button
            key={x.id}
            type="button"
            onClick={() => s.setSource(x.id)}
            className={`px-3 py-1.5 rounded-md text-sm transition-colors ${s.source === x.id ? 'bg-[#2383E2] text-white' : 'bg-[rgba(55,53,47,0.06)] dark:bg-white/10 text-[#37352F] dark:text-white hover:bg-[rgba(55,53,47,0.1)]'}`}
            {...tip(x.hint)}
          >
            {x.label}
          </button>
        ))}
      </div>

      {s.source === 'fase' && (
        <div className="grid grid-cols-2 gap-2">
          <select className={selectCls} value={s.pipelineId} onChange={(e) => { s.setPipelineId(e.target.value); s.setStageId(''); }} aria-label="Pipeline">
            <option value="">Scegli la pipeline</option>
            {pipelines.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select className={selectCls} value={s.stageId} onChange={(e) => onStage(e.target.value)} disabled={!s.pipelineId} aria-label="Fase">
            <option value="">Scegli la fase</option>
            {stages.map((st) => <option key={st.id} value={st.id}>{st.name}</option>)}
          </select>
        </div>
      )}

      {s.source === 'etichetta' && (
        <select className={selectCls} value={s.labelId} onChange={(e) => onLabel(e.target.value)} aria-label="Etichetta">
          <option value="">Scegli l'etichetta</option>
          {labels.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      )}

      <div className="rounded-lg border border-[rgba(55,53,47,0.09)] dark:border-white/10 p-3">
        {s.loadingCandidates ? (
          <p className="flex items-center gap-2 text-sm text-[#787774]"><Loader2 className="w-4 h-4 animate-spin" /> Cerco i destinatari...</p>
        ) : s.loadError ? (
          <p className="text-sm text-red-600">{s.loadError}</p>
        ) : (
          <>
            <p className="flex items-center gap-2 text-sm font-medium text-[#37352F] dark:text-white">
              <Users className="w-4 h-4 text-[#787774]" />
              {recipients.length === 1 ? '1 destinatario' : `${recipients.length} destinatari`}
              <span className="text-xs font-normal text-[#787774]">(massimo {max} per invio da questo numero)</span>
            </p>
            {Object.keys(summary).length > 0 && (
              <ul className="mt-2 space-y-0.5">
                {(Object.entries(summary) as Array<[ExclusionReason, number]>).map(([reason, n]) => (
                  <li key={reason} className="text-xs text-[#787774]">Esclusi {n}: {EXCLUSION_LABELS[reason]}</li>
                ))}
              </ul>
            )}
            {recipients.length > 0 && (
              <p className="mt-2 text-xs text-[#787774] line-clamp-2">
                {recipients.slice(0, 8).map((r) => r.name).join(', ')}{recipients.length > 8 ? ` e altri ${recipients.length - 8}` : ''}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
